import React, { forwardRef, useImperativeHandle, useEffect, useRef, useState } from 'react';
import { getStaff } from '../api/apiClient';
import './RichTextInput.css';

let _staffCache = null;
const loadStaff = () => {
  if (_staffCache) return Promise.resolve(_staffCache);
  return getStaff().then(res => { _staffCache = res.data; return _staffCache; });
};

const URL_RE = /^https?:\/\/\S+$/;

const COLORS = [
  '#000000','#555555','#e53e3e','#dd6b20','#b7791f',
  '#276749','#2b6cb0','#6b46c1','#97266d','#ffffff',
];
const COLOR_LABELS = ['Black','Gray','Red','Orange','Gold','Green','Blue','Purple','Pink','White'];

const BulletListIcon = () => (
  <svg width="15" height="13" viewBox="0 0 15 13" fill="none">
    <circle cx="2" cy="2.5" r="1.5" fill="currentColor"/>
    <line x1="5.5" y1="2.5" x2="14" y2="2.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
    <circle cx="2" cy="6.5" r="1.5" fill="currentColor"/>
    <line x1="5.5" y1="6.5" x2="14" y2="6.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
    <circle cx="2" cy="10.5" r="1.5" fill="currentColor"/>
    <line x1="5.5" y1="10.5" x2="14" y2="10.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
  </svg>
);

const LinkIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/>
    <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/>
  </svg>
);

const RichTextInput = forwardRef(({
  placeholder   = 'Type a message…',
  onChangeValue,
  className     = '',
  showToolbar   = false,
  htmlMode      = false,
  defaultHTML   = '',
}, ref) => {
  const editorRef      = useRef(null);
  const lastSelRef     = useRef(null);   // last editor selection, for size/color pickers
  const savedRangeRef  = useRef(null);   // saved range for link button
  const colorRef       = useRef(null);
  const defaultHTMLRef = useRef(defaultHTML);

  const [staffList,    setStaffList]    = useState([]);
  const [showDrop,     setShowDrop]     = useState(false);
  const [query,        setQuery]        = useState('');
  const [selIdx,       setSelIdx]       = useState(0);
  const [fmts,         setFmts]         = useState(new Set());
  const [colorOpen,    setColorOpen]    = useState(false);
  const [activeColor,  setActiveColor]  = useState('#000000');
  const [linkMode,     setLinkMode]     = useState(false);
  const [linkUrl,      setLinkUrl]      = useState('');

  useEffect(() => { loadStaff().then(setStaffList).catch(() => {}); }, []);

  // Initialize editor with defaultHTML on mount
  useEffect(() => {
    if (defaultHTMLRef.current && editorRef.current) {
      editorRef.current.innerHTML = defaultHTMLRef.current;
    }
  }, []);

  // Close color picker when clicking outside
  useEffect(() => {
    if (!colorOpen) return;
    const h = e => { if (colorRef.current && !colorRef.current.contains(e.target)) setColorOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [colorOpen]);

  const filtered = staffList
    .filter(s => s.name.toLowerCase().includes(query.toLowerCase()))
    .slice(0, 7);

  // ── Serializers ────────────────────────────────────────────────────────────
  const serializeText = () => {
    const el = editorRef.current;
    if (!el) return { text: '', mentions: [] };
    let text = '';
    const mentions = [];
    const walk = node => {
      if (node.nodeType === Node.TEXT_NODE) {
        text += node.textContent;
      } else if (node.classList?.contains('mention-pill')) {
        text += node.textContent;
        const id = parseInt(node.dataset.id, 10);
        const name = node.dataset.name;
        if (id && !mentions.find(m => m.id === id)) mentions.push({ id, name });
      } else if (node.tagName === 'BR') {
        text += '\n';
      } else if (node.tagName === 'DIV' || node.tagName === 'P') {
        node.childNodes.forEach(walk);
        if (!text.endsWith('\n')) text += '\n';
      } else {
        node.childNodes.forEach(walk);
      }
    };
    el.childNodes.forEach(walk);
    return { text: text.trim(), mentions };
  };

  const serializeHTML = () => {
    const el = editorRef.current;
    if (!el) return { html: '', mentions: [] };
    const mentions = [];
    el.querySelectorAll('.mention-pill').forEach(pill => {
      const id = parseInt(pill.dataset.id, 10);
      const name = pill.dataset.name;
      if (id && !mentions.find(m => m.id === id)) mentions.push({ id, name });
    });
    const html = el.innerHTML;
    const empty = !html || html === '<br>' || html === '<div><br></div>';
    return { html: empty ? '' : html, mentions };
  };

  const emitChange = () => {
    if (htmlMode) {
      const { html, mentions } = serializeHTML();
      onChangeValue?.(html, mentions);
    } else {
      const { text, mentions } = serializeText();
      onChangeValue?.(text, mentions);
    }
  };

  useImperativeHandle(ref, () => ({
    clear: () => {
      if (editorRef.current) {
        editorRef.current.innerHTML = '';
        onChangeValue?.('', []);
      }
    },
    getValue: () => htmlMode ? serializeHTML() : serializeText(),
    focus:    () => editorRef.current?.focus(),
    isEmpty:  () => {
      const el = editorRef.current;
      if (!el) return true;
      const h = el.innerHTML;
      return !h || h === '<br>' || h === '<div><br></div>' || !el.textContent.trim();
    },
  }));

  // ── Selection helpers ──────────────────────────────────────────────────────
  const saveLastSel = () => {
    const sel = window.getSelection();
    if (!sel?.rangeCount) return;
    const range = sel.getRangeAt(0);
    if (editorRef.current?.contains(range.startContainer)) {
      lastSelRef.current = range.cloneRange();
    }
  };

  // ── @mention trigger detection ─────────────────────────────────────────────
  const findTrigger = () => {
    const sel = window.getSelection();
    if (!sel?.rangeCount) return null;
    const range = sel.getRangeAt(0);
    if (!range.collapsed) return null;
    const { startContainer: node, startOffset: offset } = range;
    if (node.nodeType !== Node.TEXT_NODE) return null;
    const before = node.textContent.slice(0, offset);
    const atIdx  = before.lastIndexOf('@');
    if (atIdx === -1) return null;
    const q = before.slice(atIdx + 1);
    if (q.includes('\n')) return null;
    return { q, textNode: node, atOffset: atIdx, caretOffset: offset };
  };

  // ── Auto-link on space/Enter ───────────────────────────────────────────────
  const tryAutoLink = () => {
    if (!htmlMode) return;
    const sel = window.getSelection();
    if (!sel?.rangeCount) return;
    const range = sel.getRangeAt(0);
    if (!range.collapsed) return;
    const { startContainer: node, startOffset: offset } = range;
    if (node.nodeType !== Node.TEXT_NODE) return;
    const before    = node.textContent.slice(0, offset);
    const lastSpace = before.lastIndexOf(' ');
    const word      = before.slice(lastSpace + 1);
    if (!URL_RE.test(word)) return;

    const anchor = document.createElement('a');
    anchor.href        = word;
    anchor.target      = '_blank';
    anchor.rel         = 'noopener noreferrer';
    anchor.className   = 'auto-link';
    anchor.textContent = word;

    const r = document.createRange();
    r.setStart(node, lastSpace + 1);
    r.setEnd(node, offset);
    r.deleteContents();
    r.insertNode(anchor);

    const after = document.createRange();
    after.setStartAfter(anchor);
    after.collapse(true);
    sel.removeAllRanges();
    sel.addRange(after);
  };

  // ── Toolbar active state ───────────────────────────────────────────────────
  const updateFmts = () => {
    if (!showToolbar) return;
    const next = new Set();
    try {
      if (document.queryCommandState('bold'))                next.add('bold');
      if (document.queryCommandState('italic'))              next.add('italic');
      if (document.queryCommandState('underline'))           next.add('underline');
      if (document.queryCommandState('insertUnorderedList')) next.add('ul');
    } catch {}
    setFmts(next);
  };

  // ── Input handler ──────────────────────────────────────────────────────────
  const handleInput = () => {
    const trig = findTrigger();
    if (trig) { setQuery(trig.q); setShowDrop(true); setSelIdx(0); }
    else       { setShowDrop(false); }
    updateFmts();
    saveLastSel();
    emitChange();
  };

  // ── Insert @mention pill ───────────────────────────────────────────────────
  const insertMention = staff => {
    const trig = findTrigger();
    if (!trig) return;
    const sel   = window.getSelection();
    const range = sel.getRangeAt(0);
    range.setStart(trig.textNode, trig.atOffset);
    range.deleteContents();

    const pill = document.createElement('span');
    pill.className       = 'mention-pill';
    pill.dataset.id      = String(staff.id);
    pill.dataset.name    = staff.name;
    pill.contentEditable = 'false';
    pill.textContent     = `@${staff.name}`;

    const space = document.createTextNode(' ');
    range.insertNode(space);
    range.insertNode(pill);

    const after = document.createRange();
    after.setStartAfter(space);
    after.collapse(true);
    sel.removeAllRanges();
    sel.addRange(after);

    setShowDrop(false);
    editorRef.current.focus();
    emitChange();
  };

  // ── Key handler ────────────────────────────────────────────────────────────
  const handleKeyDown = e => {
    if (showDrop && filtered.length) {
      if (e.key === 'ArrowDown')  { e.preventDefault(); setSelIdx(i => Math.min(i + 1, filtered.length - 1)); return; }
      if (e.key === 'ArrowUp')    { e.preventDefault(); setSelIdx(i => Math.max(i - 1, 0)); return; }
      if (e.key === 'Enter' || e.key === 'Tab') { e.preventDefault(); insertMention(filtered[selIdx]); return; }
      if (e.key === 'Escape')     { setShowDrop(false); return; }
    }
    if (e.key === ' ' || e.key === 'Enter') tryAutoLink();
  };

  // ── Toolbar exec command (restores saved selection first) ──────────────────
  const formatCmd = (cmd, value = null) => {
    editorRef.current?.focus();
    if (lastSelRef.current) {
      try {
        const sel = window.getSelection();
        sel.removeAllRanges();
        sel.addRange(lastSelRef.current);
      } catch {}
    }
    try { document.execCommand(cmd, false, value); } catch {}
    saveLastSel();
    updateFmts();
    emitChange();
  };

  // ── Link button ────────────────────────────────────────────────────────────
  const handleLinkBtn = e => {
    e.preventDefault();
    const sel = window.getSelection();
    savedRangeRef.current = (sel?.rangeCount && !sel.isCollapsed)
      ? sel.getRangeAt(0).cloneRange()
      : null;
    setLinkUrl('');
    setLinkMode(v => !v);
  };

  const applyLink = () => {
    editorRef.current?.focus();
    if (savedRangeRef.current) {
      const sel = window.getSelection();
      sel.removeAllRanges();
      sel.addRange(savedRangeRef.current);
    }
    const raw = linkUrl.trim();
    if (raw) {
      const url = raw.startsWith('http') || raw.startsWith('mailto:') ? raw : `https://${raw}`;
      try { document.execCommand('createLink', false, url); } catch {}
      editorRef.current?.querySelectorAll('a').forEach(a => {
        a.target = '_blank';
        a.rel    = 'noopener noreferrer';
        if (!a.classList.contains('auto-link')) a.classList.add('auto-link');
      });
    }
    setLinkMode(false);
    emitChange();
  };

  return (
    <div className={`rich-input-wrapper${showToolbar ? ' rich-input--has-toolbar' : ''} ${className}`}>
      {showToolbar && (
        <>
          <div className="rte-toolbar">
            {/* Bold / Italic / Underline */}
            <button type="button" title="Bold"
              className={`rte-btn${fmts.has('bold') ? ' rte-btn--active' : ''}`}
              onMouseDown={e => { e.preventDefault(); saveLastSel(); formatCmd('bold'); }}>
              <b>B</b>
            </button>
            <button type="button" title="Italic"
              className={`rte-btn${fmts.has('italic') ? ' rte-btn--active' : ''}`}
              onMouseDown={e => { e.preventDefault(); saveLastSel(); formatCmd('italic'); }}>
              <i>I</i>
            </button>
            <button type="button" title="Underline"
              className={`rte-btn${fmts.has('underline') ? ' rte-btn--active' : ''}`}
              onMouseDown={e => { e.preventDefault(); saveLastSel(); formatCmd('underline'); }}>
              <u>U</u>
            </button>

            <div className="rte-divider" />

            {/* Bullet list */}
            <button type="button" title="Bullet list"
              className={`rte-btn${fmts.has('ul') ? ' rte-btn--active' : ''}`}
              onMouseDown={e => { e.preventDefault(); saveLastSel(); formatCmd('insertUnorderedList'); }}>
              <BulletListIcon />
            </button>

            <div className="rte-divider" />

            {/* Font color */}
            <div className="rte-color-wrap" ref={colorRef}>
              <button type="button" title="Text color" className="rte-btn"
                onMouseDown={e => { e.preventDefault(); setColorOpen(v => !v); }}>
                <span className="rte-color-a" style={{ borderBottomColor: activeColor }}>A</span>
              </button>
              {colorOpen && (
                <div className="rte-color-picker">
                  {COLORS.map((hex, i) => (
                    <button key={hex} type="button" title={COLOR_LABELS[i]}
                      className="rte-color-swatch"
                      style={{ background: hex, outline: hex === '#ffffff' ? '1px solid #ccc' : 'none' }}
                      onMouseDown={e => {
                        e.preventDefault();
                        setActiveColor(hex);
                        setColorOpen(false);
                        formatCmd('foreColor', hex);
                      }}
                    />
                  ))}
                </div>
              )}
            </div>

            <div className="rte-divider" />

            {/* Hyperlink */}
            <button type="button" title="Insert / edit link"
              className={`rte-btn${linkMode ? ' rte-btn--active' : ''}`}
              onMouseDown={handleLinkBtn}>
              <LinkIcon />
            </button>

            <div className="rte-divider" />

            {/* Font size */}
            <select className="rte-size-select" defaultValue="3"
              onMouseDown={saveLastSel}
              onChange={e => formatCmd('fontSize', e.target.value)}>
              <option value="2">Small</option>
              <option value="3">Normal</option>
              <option value="5">Large</option>
            </select>
          </div>

          {linkMode && (
            <div className="rte-link-row">
              <input
                autoFocus
                type="text"
                className="rte-link-input"
                placeholder="Paste or type a URL…"
                value={linkUrl}
                onChange={e => setLinkUrl(e.target.value)}
                onKeyDown={e => {
                  if (e.key === 'Enter')  { e.preventDefault(); applyLink(); }
                  if (e.key === 'Escape') { setLinkMode(false); }
                }}
              />
              <button type="button" className="rte-link-apply" onClick={applyLink}>Apply</button>
              <button type="button" className="rte-link-cancel" onClick={() => setLinkMode(false)}>✕</button>
            </div>
          )}
        </>
      )}

      <div
        ref={editorRef}
        className="rich-input-editor"
        contentEditable
        suppressContentEditableWarning
        onInput={handleInput}
        onKeyDown={handleKeyDown}
        onKeyUp={() => { saveLastSel(); updateFmts(); }}
        onMouseUp={() => { saveLastSel(); updateFmts(); }}
        data-placeholder={placeholder}
        role="textbox"
        aria-multiline="true"
      />

      {showDrop && filtered.length > 0 && (
        <div className="mention-dropdown">
          {filtered.map((s, i) => (
            <div key={s.id}
              className={`mention-option${i === selIdx ? ' mention-option--active' : ''}`}
              onMouseDown={e => { e.preventDefault(); insertMention(s); }}>
              <span className="mention-option-avatar">
                {s.name.trim().split(/\s+/).map(w => w[0]).join('').slice(0, 2).toUpperCase()}
              </span>
              <div className="mention-option-info">
                <span className="mention-option-name">{s.name}</span>
                {s.title && <span className="mention-option-title">{s.title}</span>}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
});

export default RichTextInput;
